import React, { useContext, useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, useTheme, Chip } from '@mui/material';
import { tokens } from '../theme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import fetchData from '../helpers/fetchData';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

interface AggregatedStats {
  current: {
    new: number;
    duplicate: number;
    error: number;
    total: number;
    revenue: number;
    successRate: number;
    errorRate: number;
    duplicateRate: number;
  };
  previous: {
    new: number;
    duplicate: number;
    error: number;
    total: number;
    revenue: number;
    successRate: number;
    errorRate: number;
    duplicateRate: number;
  };
  trends: {
    successRate: number;
    errorRate: number;
    duplicateRate: number;
    revenue: number;
    revenuePercent: number;
  };
}

interface DailyControllingKPICardsProps {
  selectedCustomers: string[];
  selectedClients: number[];
}

const DailyControllingKPICards: React.FC<DailyControllingKPICardsProps> = ({
  selectedCustomers,
  selectedClients,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [stats, setStats] = useState<AggregatedStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (
        !filterState.startDate ||
        !filterState.endDate ||
        selectedCustomers.length === 0 ||
        selectedClients.length === 0
      ) {
        setStats(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          start_date: dateToSqlDate(filterState.startDate),
          end_date: dateToSqlDate(filterState.endDate),
          customers: selectedCustomers.join(','),
          clients: selectedClients.join(','),
        };

        const data = await fetchData<AggregatedStats>(
          '/api/daily-controlling/aggregated-stats',
          options,
        );

        const flatData = Array.isArray(data) ? data[0] : data;
        setStats(flatData);
      } catch (err: any) {
        console.error('Error fetching aggregated stats:', err);
        setError(err.message || 'Failed to fetch stats');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [filterState.startDate, filterState.endDate, selectedCustomers, selectedClients]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0.5) return <TrendingUpIcon fontSize="small" />;
    if (trend < -0.5) return <TrendingDownIcon fontSize="small" />;
    return <TrendingFlatIcon fontSize="small" />;
  };

  const getTrendColor = (trend: number, inverse = false) => {
    const positive = inverse ? trend < 0 : trend > 0;
    if (Math.abs(trend) < 0.5) return colors.grey[500];
    return positive ? colors.greenAccent[500] : colors.redAccent[500];
  };

  const renderCard = (
    title: string,
    value: string,
    trend: number,
    inverseColor = false,
    subtitle?: string,
  ) => (
    <Card
      sx={{
        backgroundColor: colors.primary[400],
        borderRadius: '8px',
        height: '100%',
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ fontWeight: 'bold', color: colors.grey[100], mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: colors.grey[300], mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        <Chip
          icon={getTrendIcon(trend)}
          label={trend > 0 ? `+${formatPercentage(Math.abs(trend))}` : formatPercentage(trend)}
          size="small"
          sx={{
            backgroundColor: getTrendColor(trend, inverseColor),
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" gap={2} mb={2}>
        {[1, 2, 3, 4].map((i) => (
          <Card
            key={i}
            sx={{
              flex: 1,
              backgroundColor: colors.primary[400],
              borderRadius: '8px',
              height: '150px',
            }}
          >
            <CardContent>
              <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                Laden...
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2} mb={2} sx={{ backgroundColor: colors.redAccent[700], borderRadius: '8px' }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!stats || selectedCustomers.length === 0 || selectedClients.length === 0) {
    return null;
  }

  return (
    <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={2} mb={2}>
      {renderCard(
        'Neue Leads Prozentual',
        formatPercentage(stats.current.successRate),
        stats.trends.successRate,
        false,
        `${stats.current.new.toLocaleString('de-DE')} neue Leads`,
      )}
      {renderCard(
        'Error-Rate',
        formatPercentage(stats.current.errorRate),
        stats.trends.errorRate,
        true,
        `${stats.current.error.toLocaleString('de-DE')} Fehler`,
      )}
      {renderCard(
        'Dupletten-Quote',
        formatPercentage(stats.current.duplicateRate),
        stats.trends.duplicateRate,
        true,
        `${stats.current.duplicate.toLocaleString('de-DE')} Duplikate`,
      )}
      {renderCard(
        'Geschätzte Einnahmen',
        formatCurrency(stats.current.revenue),
        stats.trends.revenuePercent,
        false,
        `${stats.current.total.toLocaleString('de-DE')} Gesamt-Leads`,
      )}
    </Box>
  );
};

export default DailyControllingKPICards;
